import { type AnimationEvent, type ReactNode, useState } from 'react';

import styles from './CardSlot.module.scss';

interface CardSlotProps {
    /** Whether the card applies to this pen is not known yet. */
    isResolving: boolean;
    /** Once resolved: whether the card applies. */
    isVisible: boolean;
    /** Shown while resolving, and faded out if the card turns out not to apply. */
    placeholder: ReactNode;
    children: ReactNode;
}

/**
 * A place on the dashboard for a card that may not apply to the pen.
 *
 * Whether it does — a licence, the bioscope type, sensor data — is known only once a request
 * lands, so the slot shows a placeholder until then rather than sitting empty. If the card does
 * not apply, the placeholder fades and folds away instead of vanishing, which would make the
 * cards under it jump.
 */
export default function CardSlot(props: CardSlotProps) {
    const { isResolving, isVisible, placeholder, children } = props;
    const [wasResolving, setWasResolving] = useState(isResolving);
    const [isLeaving, setIsLeaving] = useState(false);

    // Adjusted during render rather than in an effect, so the frame after resolving already
    // shows the fade instead of flashing an empty slot first.
    if (wasResolving !== isResolving) {
        setWasResolving(isResolving);
        setIsLeaving(!isResolving && !isVisible);
    }

    if (isResolving) {
        return <>{placeholder}</>;
    }

    if (isVisible) {
        return <>{children}</>;
    }

    if (!isLeaving) {
        return null;
    }

    function handleAnimationEnd(event: AnimationEvent<HTMLDivElement>) {
        // The skeleton inside runs animations of its own; only the slot's own ends it.
        if (event.target === event.currentTarget) {
            setIsLeaving(false);
        }
    }

    return (
        <div className={styles.leaving} aria-hidden="true" onAnimationEnd={handleAnimationEnd}>
            <div className={styles.content}>{placeholder}</div>
        </div>
    );
}
